import { TeamCoordinationStrategy } from '@/types'
import Badge from '@/components/common/Badge'
import {
  ArrowsRightLeftIcon,
  Squares2X2Icon,
  ChartBarSquareIcon,
  UsersIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'

interface StrategySelectorProps {
  value: TeamCoordinationStrategy
  onChange: (strategy: TeamCoordinationStrategy) => void
  disabled?: boolean
}

const strategies = [
  {
    value: TeamCoordinationStrategy.SEQUENTIAL,
    label: 'Sequential',
    description: 'Agents execute one after another in order',
    icon: ArrowsRightLeftIcon,
    color: 'primary' as const,
    useCases: 'Pipelines, multi-step workflows',
  },
  {
    value: TeamCoordinationStrategy.PARALLEL,
    label: 'Parallel',
    description: 'Agents execute simultaneously',
    icon: Squares2X2Icon,
    color: 'success' as const,
    useCases: 'Independent tasks, research',
  },
  {
    value: TeamCoordinationStrategy.HIERARCHICAL,
    label: 'Hierarchical',
    description: 'Coordinator delegates to sub-agents',
    icon: ChartBarSquareIcon,
    color: 'warning' as const,
    useCases: 'Complex coordination, team management',
  },
  {
    value: TeamCoordinationStrategy.CONSENSUS,
    label: 'Consensus',
    description: 'Agents collaborate until agreement',
    icon: UsersIcon,
    color: 'error' as const,
    useCases: 'Decision making, voting',
  },
  {
    value: TeamCoordinationStrategy.FEEDBACK_LOOP,
    label: 'Feedback Loop',
    description: 'Iterative execution with validation',
    icon: ArrowPathIcon,
    color: 'gray' as const,
    useCases: 'Code validation, quality control',
  },
]

const StrategySelector = ({ value, onChange, disabled = false }: StrategySelectorProps) => {
  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-900">
        Coordination Strategy <span className="text-red-500">*</span>
      </label>
      <p className="text-sm text-gray-600">
        Choose how agents in this team will coordinate their work
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {strategies.map((strategy) => {
          const Icon = strategy.icon
          const isSelected = value === strategy.value

          return (
            <button
              key={strategy.value}
              type="button"
              onClick={() => onChange(strategy.value)}
              disabled={disabled}
              className={`
                relative p-4 border-2 rounded-lg text-left transition-all
                ${
                  isSelected
                    ? 'border-primary-500 bg-primary-50 shadow-md'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`
                    p-2 rounded-lg
                    ${isSelected ? 'bg-primary-500/15' : 'bg-gray-100'}
                  `}
                >
                  <Icon
                    className={`w-5 h-5 ${isSelected ? 'text-primary-600' : 'text-gray-600'}`}
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-gray-900">{strategy.label}</h4>
                    {isSelected && (
                      <Badge variant="primary" size="sm">
                        Selected
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-600 mb-2">{strategy.description}</p>
                  <p className="text-xs text-gray-500 italic">Use for: {strategy.useCases}</p>
                </div>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default StrategySelector
